/**
 * Device ID — anonymous client identity.
 *
 * Y1 design (per doc 03 §6, doc 04 §3): no login, no PII for children.
 * Device identity is a UUID v4 generated on first launch and persisted
 * client-side. The server uses account ownership for authorization; the
 * device id remains a telemetry and device-bound PIN identifier.
 *
 * Storage:
 *  - Web: localStorage under STORAGE_KEY (Y1; task #9 will keep this and
 *    add expo-secure-store for native).
 *  - Native (iOS/Android): falls back to in-memory only for now. Native
 *    persistence is part of task #9 — re-enable on each app launch by
 *    calling getOrCreateDeviceId() before any API call.
 *
 * Important: do NOT log device_id (UU PDP — anonymized but still
 * PII-adjacent). Treat the value as a session secret in the client.
 */

import type { AgeBracket } from '@funpaper/shared';
export type { AgeBracket };

const STORAGE_KEY = 'funpaper.deviceId';
const DEVICE_ID_REGEX = /^[a-zA-Z0-9_-]{8,64}$/;
let inMemoryDeviceId: string | null = null;

function isValidDeviceId(value: unknown): value is string {
  return typeof value === 'string' && DEVICE_ID_REGEX.test(value);
}

function generateUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for very old environments (e.g. early RN runtimes). crypto is
  // expected to be present in all modern browsers and Node 19+.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Get the persisted device id, or create + persist a new one.
 * Safe to call repeatedly: returns the same id across calls within a
 * single app launch.
 */
export function getOrCreateDeviceId(): string {
  if (isValidDeviceId(inMemoryDeviceId)) return inMemoryDeviceId;

  if (typeof localStorage !== 'undefined') {
    try {
      const existing = localStorage.getItem(STORAGE_KEY);
      if (isValidDeviceId(existing)) {
        inMemoryDeviceId = existing;
        return existing;
      }
    } catch {
      // Continue with the in-memory fallback below.
    }
  }

  const id = generateUuid();
  inMemoryDeviceId = id;
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.setItem(STORAGE_KEY, id);
    } catch {
      // localStorage may be disabled (private mode, quota). In-memory only
      // for this launch; native callers still get a stable id per launch.
    }
  }
  return id;
}

/**
 * For tests / "Hapus Data" feature (Y2). Removes the persisted device id
 * and returns a fresh one. Server-side rows remain but become orphaned.
 */
export function resetDeviceId(): string {
  inMemoryDeviceId = null;
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }
  return getOrCreateDeviceId();
}
